// Entscheidungslogik der Live-Kollaboration — bewusst ohne DOM, Cable und
// Netzwerk, damit sie ohne Browser prüfbar ist. Wer die Nachrichten entgegen-
// nimmt, steht in channels/project_channel.js; was daraus im Editor passiert,
// im editor_controller. Hier wird nur entschieden.

// Wie viele Initialen-Punkte die Präsenzanzeige zeigt, bevor sie zu „+N“
// zusammenfasst. Drei passen auch auf dem schmalsten Header noch neben den
// Speicherstatus.
export const PRESENCE_MAX_DOTS = 3

// Was mit einer Kanal-Nachricht zu tun ist:
//
//   "ignore" — nichts tun (eigene Nachricht, fremder Typ, kaputte Nachricht)
//   "reload" — Szene still nachladen (nichts Ungespeichertes in Gefahr)
//   "notify" — Hinweis zeigen und den Nutzer entscheiden lassen
//
// Die eigene Nachricht muss zuerst raus: Jeder eigene PATCH broadcastet auch an
// den Absender zurück. Würde die nicht ausgefiltert, zöge jedes Speichern ein
// Nachladen nach sich, das wiederum … — die Endlosschleife lebt genau hier.
export function collabAction({ message, selfUserId, dirty = false, inFlight = false } = {}) {
  if (message?.type !== "scene_updated") return "ignore"
  if (sameUser(message.by_user_id, selfUserId)) return "ignore"
  return dirty || inFlight ? "notify" : "reload"
}

// user_id kommt als Zahl aus Action Cable, aus dem data-Attribut aber als
// String — deshalb über Number vergleichen. Fehlt eine Seite, gilt die
// Nachricht als fremd: lieber einmal zu viel nachziehen als einen fremden
// Stand verpassen.
function sameUser(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false
  return Number(a) === Number(b)
}

// Name für die Statusmeldung „Von <Name> aktualisiert“. Der Server schickt
// derzeit die E-Mail-Adresse; ohne brauchbaren Namen bleibt es allgemein.
export function updatedByLabel(message) {
  const by = typeof message?.by === "string" ? message.by.trim() : ""
  return by ? `Von ${by} aktualisiert` : "Von jemand anderem aktualisiert"
}

// Die anderen Bearbeiter: Der Server schickt die Präsenzliste inklusive des
// eigenen Eintrags (er weiß nicht, wer gerade zuhört), also selbst filtern.
// Die Sortierung nach Name steht bereits, sie wird nur nicht zerstört.
export function otherUsers(users, selfUserId) {
  if (!Array.isArray(users)) return []
  return users.filter((user) => user && !sameUser(user.user_id, selfUserId))
}

// Anzeigename eines Präsenzeintrags — name, sonst E-Mail, sonst Platzhalter.
export function userLabel(user) {
  const name = (user?.name || user?.email_address || "").trim()
  return name || "Unbekannt"
}

// Initialen für den Punkt: „anna.mueller@…“ → „AM“, „schreinerei@…“ → „SC“.
// Bewusst aus dem Teil vor dem @ gebildet, sonst hießen alle Kollegen einer
// Werkstatt gleich.
export function initialsOf(user) {
  const label = userLabel(user)
  const local = label.split("@")[0]
  const words = local.split(/[.\-_\s]+/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

// Text für Screenreader und Tooltip-Vorspann. Leer = niemand sonst da,
// die Anzeige bleibt dann komplett unsichtbar.
export function presenceLabel(others) {
  const count = others.length
  if (count === 0) return ""
  return count === 1 ? "1 weiterer Bearbeiter" : `${count} weitere Bearbeiter`
}

// Fertig aufbereitete Präsenzanzeige: Punkte, Überlauf, Beschriftung, Tooltip.
// Der Controller hängt daraus nur noch Elemente in den Header.
export function presenceSummary(users, selfUserId) {
  const others = otherUsers(users, selfUserId)
  const shown = others.slice(0, PRESENCE_MAX_DOTS)
  return {
    count: others.length,
    dots: shown.map((user) => ({ initials: initialsOf(user), name: userLabel(user) })),
    overflow: Math.max(0, others.length - shown.length),
    label: presenceLabel(others),
    title: others.map(userLabel).join(", ")
  }
}
